import cx from "classnames";
import { t } from "ttag";

import {
  skipToken,
  useGetDatabaseQuery,
  useListDatabaseSchemasQuery,
  useListDatabasesQuery,
} from "metabase/api";
import { TableBrowser } from "metabase/browse/tables/TableBrowser";
import { BrowserCrumbs } from "metabase/common/components/BrowserCrumbs";
import { NotFound } from "metabase/common/components/ErrorPages";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { findDatabaseByName } from "metabase/common/utils/database";
import CS from "metabase/css/core/index.css";
import { useParams } from "metabase/router";
import { Flex } from "metabase/ui";
import * as Urls from "metabase/urls";
import type { DatabaseId } from "metabase-types/api";

import { BrowseCard } from "../components/BrowseCard";
import S from "../components/BrowseContainer.module.css";
import { BrowseDataHeader } from "../components/BrowseDataHeader";
import { BrowseGrid } from "../components/BrowseGrid";

type Schema = { id: string; name: string };

const BrowseSchemasContainer = ({
  schemas,
  dbId,
  params,
}: {
  schemas: Schema[];
  dbId: DatabaseId;
  params: any;
}) => {
  const isSingleSchema = schemas.length === 1;
  const { data: database } = useGetDatabaseQuery(
    isSingleSchema ? skipToken : { id: dbId },
  );

  return (
    <Flex
      className={S.browseContainer}
      flex={1}
      direction="column"
      wrap="nowrap"
      pt="md"
      data-testid="browse-schemas"
    >
      <BrowseDataHeader />
      <Flex className={S.browseMain} direction="column" wrap="nowrap" flex={1}>
        <Flex maw="64rem" mx="auto" w="100%" direction="column">
          {isSingleSchema ? (
            <TableBrowser
              params={params}
              dbId={dbId}
              schemaName={schemas[0].name}
              // hide the schema since there's only one
              showSchemaInHeader={false}
            />
          ) : (
            <>
              <Flex align="center" pt="md" pr="sm" pb="sm">
                <BrowserCrumbs
                  crumbs={[
                    { title: t`Databases`, to: "/browse/databases" },
                    { title: database?.name ?? "" },
                  ]}
                />
              </Flex>
              {schemas.length === 0 ? (
                <h2
                  className={cx(CS.full, CS.textCentered, CS.textMedium)}
                >{t`This database doesn't have any tables.`}</h2>
              ) : (
                <BrowseGrid pt="lg">
                  {schemas.map((schema) => (
                    <BrowseCard
                      key={schema.id}
                      title={schema.name}
                      icon="folder"
                      to={Urls.browseSchemaBySlug(params.slug, schema.name)}
                    />
                  ))}
                </BrowseGrid>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

const BrowseSchemasByDatabaseName = ({
  databaseName,
  params,
}: {
  databaseName: string;
  params: { slug: string };
}) => {
  const { data, isLoading, error } = useListDatabasesQuery();
  const database = findDatabaseByName(data?.data ?? [], databaseName);

  if (isLoading || error) {
    return <LoadingAndErrorWrapper loading={isLoading} error={error} />;
  }

  if (!database) {
    return <NotFound />;
  }

  return <BrowseSchemasForDatabase dbId={database.id} params={params} />;
};

export const BrowseSchemas = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const params = { slug };
  const dbId = Urls.extractEntityId(slug);

  if (dbId == null) {
    return <BrowseSchemasByDatabaseName databaseName={slug} params={params} />;
  }

  return <BrowseSchemasForDatabase dbId={dbId} params={params} />;
};

const BrowseSchemasForDatabase = ({
  dbId,
  params,
}: {
  dbId: number;
  params: { slug: string };
}) => {
  const { data, isLoading, error } = useListDatabaseSchemasQuery({ id: dbId });

  if (isLoading || error) {
    return <LoadingAndErrorWrapper loading={isLoading} error={error} />;
  }

  const schemas: Schema[] = (data ?? []).map((name) => ({ id: name, name }));

  return (
    <BrowseSchemasContainer schemas={schemas} dbId={dbId} params={params} />
  );
};
